import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { Observable, of } from 'rxjs';
import { take, tap } from 'rxjs/operators';

/**
 * Guard para controle de acesso baseado em funcionalidades.
 *
 * O acesso é decidido pela lista de funcionalidades do usuário retornada pelo back-end
 * (ver `PermissaoService`). A rota deve informar o código da funcionalidade em `data.featureCode`.
 *
 * Uso no routing:
 * ```typescript
 * {
 *   path: 'minha-rota',
 *   canActivate: [AuthGuard, FeatureAccessGuard],
 *   data: { featureCode: 'GUIAS_TISS.LISTAGEM_GUIAS' }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class FeatureAccessGuard implements CanActivate {
  constructor(
    private notificationService: NotificationService,
    private permissaoService: PermissaoService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const featureCode: string = route.data['featureCode'] || '';

    // Se não há featureCode definido, a rota não é protegida por funcionalidade.
    if (!featureCode) {
      return of(true);
    }

    return this.permissaoService.checkAcessoFeature(featureCode).pipe(
      tap(hasAcesso => {
        if (!hasAcesso) {
          this.notificationService.warn('Esta funcionalidade não está disponível para o seu módulo.');
        }
      }),
      take(1),
    );
  }
}
